import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaSearch, FaFilter, FaBook, FaClock, FaGraduationCap } from 'react-icons/fa';
import { getEnrolledCourses } from '../../features/courses/courseSlice';
import { getUserProgress } from '../../features/progress/progressSlice';

const MyCourses = () => {
  const dispatch = useDispatch();
  const { courses, isLoading } = useSelector((state) => state.courses);
  const { progress } = useSelector((state) => state.progress);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, in-progress, completed
  const [sortBy, setSortBy] = useState('recent'); // recent, title, progress
  
  useEffect(() => {
    dispatch(getEnrolledCourses());
    dispatch(getUserProgress());
  }, [dispatch]);
  
  // Filter and sort courses
  const filteredCourses = courses.filter(course => {
    // Search filter
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    if (filter === 'all') return matchesSearch;
    
    const courseProgress = progress.find(p => p.courseId === course._id);
    const completionPercentage = courseProgress ? courseProgress.completionPercentage : 0;
    
    if (filter === 'completed' && completionPercentage === 100) return matchesSearch;
    if (filter === 'in-progress' && completionPercentage < 100) return matchesSearch;
    
    return false;
  });
  
  // Sort courses
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (sortBy === 'title') {
      return a.title.localeCompare(b.title);
    }
    
    if (sortBy === 'progress') {
      const progressA = progress.find(p => p.courseId === a._id)?.completionPercentage || 0;
      const progressB = progress.find(p => p.courseId === b._id)?.completionPercentage || 0;
      return progressB - progressA;
    }
    
    // Default: sort by enrollment date (recent)
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">My Courses</h1>
      
      {/* Search and filters */}
      <div className="bg-white container-rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search courses..."
              className="pl-10 pr-4 py-2 border container-rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <FaFilter className="text-gray-400 mr-2" />
              <select
                className="border container-rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Courses</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            <div className="flex items-center">
              <span className="text-gray-400 mr-2">Sort:</span>
              <select
                className="border container-rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recent">Most Recent</option>
                <option value="title">Title (A-Z)</option>
                <option value="progress">Progress</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Course list */}
      {sortedCourses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedCourses.map((course) => {
            const courseProgress = progress.find(p => p.courseId === course._id);
            const completionPercentage = courseProgress ? courseProgress.completionPercentage : 0;
            
            return (
              <div key={course._id} className="bg-white container-rounded-lg shadow overflow-hidden">
                {/* Course image */}
                <div className="h-40 bg-gray-200 relative">
                  {course.image ? (
                    <img 
                      src={course.image} 
                      alt={course.title} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <FaBook className="text-4xl text-gray-400" />
                    </div>
                  )}
                  
                  {/* Progress badge */}
                  <div className="absolute top-2 right-2 bg-white rounded-full px-2 py-1 text-xs font-medium shadow">
                    {completionPercentage === 100 ? (
                      <span className="text-green-600 flex items-center">
                        <FaGraduationCap className="mr-1" />
                        Completed
                      </span>
                    ) : (
                      <span className="text-blue-600 flex items-center">
                        <FaClock className="mr-1" />
                        {completionPercentage}% Complete
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Course content */}
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-1">{course.title}</h3>
                  <p className="text-sm text-gray-500 mb-2">{course.instructor}</p>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                    <div 
                      className="bg-primary h-2.5 rounded-full" 
                      style={{ width: `${completionPercentage}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {courseProgress?.completedLessons || 0} / {course.totalLessons || 0} lessons
                    </span>
                    
                    <Link 
                      to={`/dashboard/courses/${course._id}`}
                      className="text-primary hover:underline text-sm font-medium"
                    >
                      {completionPercentage === 100 ? 'Review Course' : 'Continue'}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white container-rounded-lg shadow p-8 text-center">
          <FaBook className="text-5xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium mb-2">No courses found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || filter !== 'all' ? 
              'Try adjusting your search or filters' : 
              'You haven\'t enrolled in any courses yet'}
          </p>
          {!searchTerm && filter === 'all' && (
            <Link to="/courses" className="btn-primary inline-block">
              Browse Courses
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default MyCourses;